/**
 * CR426 + CR428: Hooks for live deal picker in editor tabs.
 * 
 * useDealCardEditorLiveDeals — lightweight list for picker dropdown
 * useDealCardEditorLiveDeal — full deal via render payload RPC (CR428)
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useDealCardRenderPayload, type RenderPayloadMeta } from "./useDealCardRenderPayload";
import type { EditorSampleDealMeta } from "./useDealCardEditorSampleDeal";

// ================================================================
// TYPES
// ================================================================

export interface LiveDealListItem {
  deal_id: string;
  headline: string;
  merchant_name: string;
  deal_type: string;
  updated_at: string;
  dedupe_key: string | null;
  computed_value_cad: number | null;
  effective_percent_back: number | null;
  qualifying_spend_cad: number | null;
  discount_amount_cad: number | null;
  discount_percent: number | null;
  points_amount: number | null;
  start_date: string | null;
  end_date: string | null;
}

export interface LiveDealResult {
  deal: Record<string, unknown> | null;
  meta: EditorSampleDealMeta | null;
  loading: boolean;
  error: string | null;
}

// ================================================================
// LIST HOOK
// ================================================================

export function useDealCardEditorLiveDeals(
  environment: string = "staging",
  dealType: string | null = null,
  search: string = "",
  limit: number = 50
) {
  const [deals, setDeals] = useState<LiveDealListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!dealType) {
      setDeals([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc(
        "get_deal_card_editor_live_deals" as any,
        {
          p_environment: environment,
          p_deal_type: dealType,
          p_limit: limit,
          p_search: search || null,
        }
      );

      if (rpcError) throw new Error(rpcError.message);
      setDeals((data as LiveDealListItem[]) ?? []);
    } catch (err) {
      console.error("[useDealCardEditorLiveDeals] Error:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, [environment, dealType, search, limit]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return useMemo(() => ({ deals, loading, error, refetch: fetch }), [deals, loading, error, fetch]);
}

// ================================================================
// SINGLE DEAL HOOK (CR428: via render payload)
// ================================================================

export function useDealCardEditorLiveDeal(environment: string = "staging", dealId: string | null): LiveDealResult {
  // CR428: Use render payload for hardened derived fields
  const renderPayload = useDealCardRenderPayload(environment, dealId);

  // Build EditorSampleDealMeta from render payload meta
  const meta = useMemo<EditorSampleDealMeta | null>(() => {
    if (!renderPayload.meta) return null;
    const rpm = renderPayload.meta;
    return {
      source: "auto" as const,
      deal_id: rpm.deal_id,
      dedupe_key: rpm.dedupe_key_present ? "(from_meta)" : null,
      deal_type: rpm.deal_type ?? "UNCLASSIFIED",
      environment: rpm.environment,
      computed_value_cad: null, // In deal_json now
      effective_percent_back: null,
      eligibility_requirements: null,
      updated_at: rpm.updated_at,
      renderPayload: rpm,
    };
  }, [renderPayload.meta]);

  return useMemo(() => ({
    deal: renderPayload.deal,
    meta,
    loading: renderPayload.loading,
    error: renderPayload.error,
  }), [renderPayload.deal, meta, renderPayload.loading, renderPayload.error]);
}
